import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Wallet, Save, RotateCcw, CheckCircle2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { hotels as hotelsApi } from '@/lib/api';

const CHAIN_INFO = [
  { key: 'ethereum', label: 'Ethereum', symbol: 'ETH', type: 'L1', color: 'bg-slate-100 text-slate-700' },
  { key: 'base', label: 'Base', symbol: 'ETH', type: 'L2', color: 'bg-blue-100 text-blue-700' },
  { key: 'polygon', label: 'Polygon', symbol: 'MATIC', type: 'L2', color: 'bg-purple-100 text-purple-700' },
  { key: 'bitcoin', label: 'Bitcoin', symbol: 'BTC', type: 'L1', color: 'bg-orange-100 text-orange-700' },
  { key: 'solana', label: 'Solana', symbol: 'SOL', type: 'L1', color: 'bg-teal-100 text-teal-700' }
];

export const WalletAddresses = ({ hotelId, readOnly = false }) => {
  const [addresses, setAddresses] = useState({
    ethereum: '',
    base: '',
    polygon: '',
    bitcoin: '',
    solana: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    loadWalletAddresses();
  }, [hotelId]);

  const loadWalletAddresses = async () => {
    try {
      setLoading(true);
      const data = await hotelsApi.getWalletAddresses(hotelId);
      if (data.wallet_addresses) {
        setAddresses(prev => ({ ...prev, ...data.wallet_addresses }));
      }
    } catch (error) {
      console.error('Failed to load wallet addresses:', error);
      toast.error('Failed to load wallet addresses');
    } finally {
      setLoading(false);
    }
  };

  const handleAddressChange = (chain, value) => {
    setAddresses(prev => ({ ...prev, [chain]: value }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await hotelsApi.updateWalletAddresses(hotelId, addresses);
      toast.success('Wallet addresses saved successfully');
      setHasChanges(false);
    } catch (error) {
      console.error('Failed to save wallet addresses:', error);
      toast.error('Failed to save wallet addresses');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    loadWalletAddresses();
    setHasChanges(false);
  };

  const getFilledCount = () => {
    return Object.values(addresses).filter(addr => addr && addr.trim()).length;
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="py-8">
          <div className="flex items-center justify-center">
            <div className="text-muted-foreground">Loading wallet addresses...</div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="settings-wallet-addresses-section">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
              <Wallet className="w-5 h-5 text-primary" />
            </div>
            <div>
              <CardTitle className="heading-font">Wallet Addresses</CardTitle>
              <CardDescription className="mt-1">
                {readOnly 
                  ? 'View your configured wallet addresses for crypto payments. To edit, go to Settings.'
                  : 'Add a receiving address for each blockchain you want to accept. You can start with one and add more later.'
                }
              </CardDescription>
            </div>
          </div>
          <Badge variant="outline" className="text-xs">
            {getFilledCount()}/5 configured
          </Badge>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-6">
        <Alert className="bg-accent/30 border-accent">
          <AlertCircle className="w-4 h-4 text-accent-foreground" />
          <AlertDescription className="text-sm">
            {readOnly
              ? 'These are your current wallet addresses. Guests will send payments directly to these addresses.'
              : 'Guests will send payments directly to these addresses. Double-check each address before saving.'
            }
          </AlertDescription>
        </Alert>

        <div className="space-y-4">
          {CHAIN_INFO.map((chain) => (
            <div key={chain.key} className="space-y-2">
              <div className="flex items-center gap-2">
                <Label htmlFor={`wallet-${chain.key}`} className="text-sm font-medium">
                  {chain.label}
                </Label>
                <Badge variant="secondary" className={`text-[10px] px-1.5 py-0 ${chain.color}`}>
                  {chain.type}
                </Badge>
                <span className="text-xs text-muted-foreground mono">{chain.symbol}</span>
              </div>
              
              <div className="relative">
                <Input
                  id={`wallet-${chain.key}`}
                  data-testid={`settings-wallet-address-input-${chain.key}`}
                  value={addresses[chain.key]}
                  onChange={(e) => handleAddressChange(chain.key, e.target.value)}
                  placeholder={readOnly ? (addresses[chain.key] ? '' : 'Not configured') : `Enter your ${chain.label} wallet address`}
                  className="font-mono text-sm pr-10"
                  readOnly={readOnly}
                  disabled={readOnly}
                />
                {addresses[chain.key] && (
                  <CheckCircle2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-emerald-600" />
                )}
              </div>
            </div>
          ))}
        </div>

        {!readOnly && (
          <div className="flex items-center gap-3 pt-4">
            <Button
              onClick={handleSave}
              disabled={!hasChanges || saving}
              className="flex-1"
              data-testid="settings-wallet-addresses-save-button"
            >
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Saving...' : 'Save Wallet Addresses'}
            </Button>
            
            {hasChanges && (
              <Button
                variant="outline"
                onClick={handleReset}
                disabled={saving}
                data-testid="settings-wallet-addresses-reset-button"
              >
                <RotateCcw className="w-4 h-4 mr-2" />
                Reset
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
